import React, { useState, useRef, useEffect, useCallback } from "react";
import { connect } from 'react-redux';
import { Formik } from 'formik';

import { BrowserTabTitle, LoadingOverlay } from '../../../../components/display';
import { FormikControl, DateRangeSelector, checkResponses } from '../../../../components/form';
import { formatDateApi } from '../../../../components/functions';
import { Table } from '../../../../components/table';
import { Socket } from '../../../../components/ws';

import { fetchFlagsDaterange, fetchRpCentersAll, fetchStudentsAll } from '../../../../actions';
import { getDataArray } from '../../../../components/functions';

const pageTitle = 'Flags';

const defaultSorted = { dataField: 'studentName', order: 'asc' };

const start = new Date();
start.setMonth(start.getMonth() - 2);
const startApi = formatDateApi(start);
const endApi = formatDateApi(new Date());

function Flags(props){
    const mounted = useRef(false);
    useEffect(() => {
        mounted.current = true;
        return () => (mounted.current = false);
    });
    const formRef = useRef();

    const [hasLoaded, setHasLoaded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [apiError, setApiError] = useState(false);
    const [flags, setFlags] = useState([]);
    const [filteredFlags, setFilteredFlags] = useState([]);
    const [centers, setCenters] = useState([]);
    const [students, setStudents] = useState([]);

    const { fetchFlagsDaterange, fetchRpCentersAll, fetchStudentsAll } = props;

    const filterFlags = useCallback((unfilteredFlags, filterQuery) => {
        if (!filterQuery || filterQuery.trim() === '') {
            setFilteredFlags(unfilteredFlags);
            return;
        }
        const query = filterQuery.toLowerCase().trim();
        const filtered = unfilteredFlags.filter(f => {
            return (f.studentName && f.studentName.toLowerCase().includes(query)) ||
                   (f.notes && f.notes.toLowerCase().includes(query));
        });
        setFilteredFlags(filtered);
    }, []);

    const refreshData = useCallback(() => {
        (async function refresh(){
            if(loading || !formRef.current?.values) return;
            if(mounted.current) setLoading(true);

            try {
                const { startDate, endDate } = formRef.current.values;
                const flagsRes = await fetchFlagsDaterange({ startDate, endDate });
                const centersRes = await fetchRpCentersAll();
                const studentsRes = await fetchStudentsAll();
                
                const isApiError = checkResponses(flagsRes, centersRes, studentsRes);
                if(isApiError){
                    if(mounted.current){
                        setApiError('Error fetching data from the server. Please try again later.');
                        setLoading(false);
                        setHasLoaded(true);
                    }
                    return;
                } else setApiError(false);

                const newFlags = getDataArray(flagsRes);
                const newCenters = getDataArray(centersRes);
                const newStudents = getDataArray(studentsRes);

                const centerMap = {};
                newCenters.forEach(c => centerMap[parseInt(c.id)] = c.name);
                const studentMap = {};
                newStudents.forEach(s => studentMap[s.user_id] = `${s.first_name} ${s.last_name}`);

                const flagsAppended = newFlags.map(f => {
                    f.centerName = centerMap[parseInt(f.center)] || `Unknown center (ID: ${f.center})`;
                    f.studentName = studentMap[f.user] || `Unknown student (ID: ${f.user})`;
                    return f;
                });

                const { filterQuery } = formRef.current.values;

                if(mounted.current){
                    setFlags(flagsAppended);
                    setCenters(newCenters);
                    setStudents(newStudents);
                    filterFlags(flagsAppended, filterQuery);
                    setHasLoaded(true);
                    setLoading(false);
                }
            } catch (error) {
                console.error('[FLAGS] Error:', error);
                if(mounted.current){
                    setApiError('Error fetching data. Please try again later.');
                    setLoading(false);
                    setHasLoaded(true);
                }
            }
        })();
    }, [mounted, loading, fetchFlagsDaterange, fetchRpCentersAll, fetchStudentsAll, filterFlags]);

    useEffect(() => {
        refreshData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getColumns = () => [
        { dataField: 'studentName', text: 'Student', sort: true },
        { dataField: 'centerName', text: 'Center', sort: true },
        { dataField: 'notes', text: 'Notes' },
        {
            dataField: 'status',
            text: 'Status',
            sort: true,
            formatter: (row) => (
                <span className={row.status === 'Pending' ? 'text-orange-600' : 'text-green-600'}>
                    {row.status || 'Pending'}
                </span>
            )
        }
    ];

    return (
        <div className="page-box">
            <BrowserTabTitle>{pageTitle}</BrowserTabTitle>
            {loading && <LoadingOverlay/>}
            <div className="card">
                <Formik
                    enableReinitialize
                    initialValues={{
                        startDate: startApi,
                        endDate: endApi,
                        filterQuery: '',
                    }}
                    innerRef={formRef}
                    onSubmit={() => null}
                >
                    {formik => (
                        <form onSubmit={formik.handleSubmit}>
                            <h2>{pageTitle}</h2>
                            <br/>
                            <div className="flex flex-row gap-x-4 items-center mb-4">
                                <DateRangeSelector
                                    id="flags-drs"
                                    startName="startDate"
                                    endName="endDate"
                                    startLabel="Start Date"
                                    endLabel="End Date"
                                    startValue={formik.values.startDate}
                                    endValue={formik.values.endDate}
                                    defaultValid={true}
                                    onStartChange={(e) => {
                                        formik.handleChange(e);
                                        refreshData();
                                    }}
                                    onEndChange={(e) => {
                                        formik.handleChange(e);
                                        refreshData();
                                    }}
                                />
                                <div className="grid grid-cols-1 gap-y-2 w-1/3">
                                    <FormikControl
                                        id="flags-searchQuery"
                                        name="filterQuery"
                                        placeholder="Search flags..."
                                        value={formik.values.filterQuery}
                                        onChange={(e) => {
                                            formik.handleChange(e);
                                            filterFlags(flags, e.target.value);
                                        }}
                                    />
                                </div>
                            </div>
                            {apiError ? <div className="text-mpLRed">{apiError}</div> :
                                hasLoaded &&
                                <Table
                                    tableName="flags"
                                    data={filteredFlags}
                                    columns={getColumns()}
                                    defaultSorted={defaultSorted}
                                    emptyMessage="No flags found"
                                />
                            }
                        </form>
                    )}
                </Formik>
            </div>
            <Socket refreshData={refreshData} page={pageTitle} setVersion={props.setVersion} />
        </div>
    );
};

export default connect(null, { fetchFlagsDaterange, fetchRpCentersAll, fetchStudentsAll })(Flags);

